import { Button, ErrorState } from '@gymcoach/ui';
import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { applyBrand } from '@/app/brand';
import { FullScreenMessage, Splash } from '@/shell/FullScreen';
import { useTheme } from '@/shell/theme';

import { useAuth } from './AuthProvider';
import { authCopy } from './copy';
import { ForcedPasswordChange } from './ForcedPasswordChange';
import { MeContext, useMeQuery } from './useMe';

/** My account → change password (by choice). A temporary password never gets here:
 *  RequireAuth shows the forced change in place of every screen instead. */
export const CHANGE_PASSWORD_PATH = '/account/change-password';

const LandingPage = lazy(async () => ({ default: (await import('@/routes/landing/LandingPage')).LandingPage }));

/**
 * The signed-in boundary of the CRM. In order:
 *   1. no session → the landing page at /, else /login (and back here afterwards);
 *   2. load the profile and gym (useMe) — a calm splash, not a blank page;
 *   3. refuse members (they use the app) and deactivated accounts, with a way out;
 *   4. apply the gym's brand colour;
 *   5. a temporary password → "Choose your own password" in place of every screen,
 *      in the sign-in frame (no menu), until the database clears the flag.
 *
 * A convenience, not a control: RLS protects the data whatever this decides.
 */
export function RequireAuth() {
  const { session, isLoading, signOut } = useAuth();
  const location = useLocation();
  const me = useMeQuery();
  const { theme } = useTheme();
  const brand = me.data?.gym.brand_colour ?? null;

  useEffect(() => {
    applyBrand(brand, theme);
    return () => applyBrand(null, theme);
  }, [brand, theme]);

  if (isLoading) return <Splash />;
  if (!session) {
    // The front door: a signed-out visitor at / sees what Fitnatic is, not a sign-in form.
    if (location.pathname === '/')
      return (
        <Suspense fallback={<Splash />}>
          <LandingPage />
        </Suspense>
      );
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (me.isPending) return <Splash label="Loading your gym" />;

  const signOutButton = (
    <Button variant="secondary" onClick={() => void signOut()}>
      Sign out
    </Button>
  );

  if (me.isError) {
    const notFound = (me.error as { code?: string } | null)?.code === 'P0002';
    if (notFound) {
      return (
        <FullScreenMessage title={authCopy.inactive.title} actions={signOutButton}>
          {authCopy.inactive.body}
        </FullScreenMessage>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
        <ErrorState error={me.error} onRetry={() => void me.refetch()} />
      </div>
    );
  }

  const data = me.data;
  if (data.role === 'client') {
    return (
      <FullScreenMessage title="This is the gym's staff app" actions={signOutButton}>
        Members use the Fitnatic app on their phone to see their workouts, diet and membership.
      </FullScreenMessage>
    );
  }

  if (data.profile.must_change_password) {
    // Whatever address they opened, they land on it once the password is theirs.
    return (
      <MeContext.Provider value={data}>
        <ForcedPasswordChange />
      </MeContext.Provider>
    );
  }

  return (
    <MeContext.Provider value={data}>
      <Outlet />
    </MeContext.Provider>
  );
}
